import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FirmModel, GenericResModel, UserModel } from 'countable@model';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, delay, map, Observable, of, ReplaySubject, retryWhen, take } from 'rxjs';
import { environment } from '../../environments/environment';
import { AddressV2Model } from '../model/address-v2.model';
import { AiFirmModel } from '../model/billing/ai-luca.model';
import { EngCountModel } from '../model/eng/eng-count.model';
import { AddMemberModel } from '../model/firm/add-member.model';
import { AssignedFirmsModel } from '../model/firm/assigned-firms.model';
import { FirmDeleteFeedBackModel } from '../model/firm/firm-delete-feedback';
import { FirmPartnerModel } from '../model/firm/firm-partner.model';
import { FirmTeamCountModel } from '../model/firm/firm-team-count.model';
import { FirmTeamModel } from '../model/firm/firm-team.model';
import { FirmUserModel } from '../model/firm/firm-user.model';
import { OnboardingResModel } from '../model/firm/onboarding-res.model';
import { OnboardingModel } from '../model/firm/onboarding.model';
import { UserSelectedHeaderModel } from '../model/firm/user-selected-header.model';
import { UserSelectedSignatureModel } from '../model/firm/user-selected-signature.model';
import { AuthService } from './auth.service';
import { EncodingService } from './encoding.service';
import { StorageService } from './storage.service';

@Injectable({providedIn: 'root'})
export class FirmService {

  private static readonly FIRM_KEY = 'firm-key';
  private static readonly URI_FIRM = environment.apiV2 + '/firm';
  private static readonly URI_ADDRESS = environment.apiV1 + '/address';
  private static readonly URI_CLIENT_AND_ENG_DATA = environment.apiV1 + '/clients-and-eng-data';
  private static readonly URI_TIME_TRACKING_ACL = environment.apiV1 + '/access-time';
  private static readonly URI_ASSIGNED_FIRMS_OF_A_USER = FirmService.URI_FIRM + '/assigned-firms';
  private static readonly URI_ALL_PARTNER_LIST = FirmService.URI_FIRM + '/partner-assignees';
  private static readonly URI_ALL_TEAM_LIST = FirmService.URI_FIRM + '/team-assignees';

  public readonly subject = new ReplaySubject<FirmModel>(1);
  public readonly aiInfo: BehaviorSubject<AiFirmModel> = new BehaviorSubject<AiFirmModel>(null);

  constructor(private http: HttpClient, private authService: AuthService, private toaster: ToastrService,
              private encodingService: EncodingService) {
    if (localStorage.getItem(FirmService.FIRM_KEY) && localStorage.getItem(FirmService.FIRM_KEY) != 'undefined') {
      StorageService.applicationModel.firm = JSON.parse(localStorage.getItem(FirmService.FIRM_KEY));
      this.subject.next(StorageService.applicationModel.firm);
    }
    this.authService.subjectUser.pipe(takeUntilDestroyed()).subscribe(() => {
      this.refresh();
    });
  }

  public refresh(): void {
    this.getFirm().subscribe();
    this.folderSize(true).subscribe();
    this.getEngCount(true).subscribe();
    this.getTeamCountForBilling(true).subscribe();
  }

  public getFirm(): Observable<FirmModel> {
    return this.http.get<FirmModel[]>(FirmService.URI_FIRM + '/get')
      .pipe(map(firms => {
        if (!(firms && Array.isArray(firms) && firms.length > 0)) {
          throw new Error('Please wait until we fetch the firm info');
        }
        return this.processFirms(firms);
      }), retryWhen(errors => errors.pipe(take(5), delay(2500))));
  }

  public getClientsAndEngagementsData(userAccountId: number, accessLevelId: number, clients: number[]): Observable<string> {
    let extra = {
      params: {}
    };
    if (userAccountId) {
      extra['params']['user'] = userAccountId;
    }
    if (accessLevelId) {
      extra['params']['access'] = accessLevelId;
    }
    return this.http.post<GenericResModel<string>>(FirmService.URI_CLIENT_AND_ENG_DATA, clients, extra).pipe(
      map(res => {
        if (res.status !== 200 && res.status !== 204) {
          res.errors.forEach(err => this.toaster.warning(err));
        }
        return res.data;
      })
    );
  }

  public getTimeTrackingAcl(userAccountId: number, inList: string): Observable<GenericResModel<string>> {
    return this.http.post<GenericResModel<string>>(FirmService.URI_TIME_TRACKING_ACL, inList,
      {params: {'user': userAccountId}});
  }

  public inviteAll(invitees): Observable<GenericResModel<any>> {
    return this.http.put<GenericResModel<any>>(FirmService.URI_FIRM + '/invite-all', invitees, { headers: { responseType: 'json' } });
  }

  public getAddress(): Observable<AddressV2Model> {
    return this.http.get<AddressV2Model>(FirmService.URI_ADDRESS + '/firm');
  }

  public updateTeamMemberWithPermission(data: any): Observable<GenericResModel<boolean>> {
    return this.http.put<GenericResModel<boolean>>(FirmService.URI_FIRM + '/member', data);
  }

  public updateFirm(data: any): Observable<GenericResModel<boolean>> {
    return this.http.put<GenericResModel<boolean>>(FirmService.URI_FIRM + '/update', data)
      .pipe(map(incoming => {
        if (incoming.data) { this.refresh(); }
        return incoming;
      }));
  }

  public getEngCount(isRefresh?: boolean): Observable<EngCountModel> {
    isRefresh ? StorageService.applicationModel.EngCount = null : ''; // isRefresh=true to get latest data from API
    if (StorageService.applicationModel.EngCount) {
      return of(StorageService.applicationModel.EngCount);
    }
    return this.http.get<GenericResModel<EngCountModel>>(FirmService.URI_FIRM + '/count-eng')
      .pipe(map(res => {
        StorageService.applicationModel.EngCount = res.data;
        return StorageService.applicationModel.EngCount;
      }));
  }

  public getTeamCountForBilling(isRefresh?: boolean): Observable<FirmTeamCountModel> {
    isRefresh ? StorageService.applicationModel.firmTeamCount = null : ''; // isRefresh=true to get latest data from API
    if (StorageService.applicationModel.firmTeamCount) {
      return of(StorageService.applicationModel.firmTeamCount);
    }
    return this.http.get<GenericResModel<FirmTeamCountModel>>(FirmService.URI_FIRM + '/count-team')
      .pipe(map(res => {
        StorageService.applicationModel.firmTeamCount = res.data;
        return StorageService.applicationModel.firmTeamCount;
      }));
  }

  public deleteFirm(type: string, data: FirmDeleteFeedBackModel): Observable<GenericResModel<boolean>> {
    return this.http.delete<GenericResModel<boolean>>(FirmService.URI_FIRM + '/delete',
      {headers: {'X-Type': type}, body: data}).pipe(map(incoming => {
      if (incoming.status === 200 && type === 'next_billing_cycle') { this.refresh(); }
      return incoming;
    }));
  }

  public addTeamMember(data: AddMemberModel): Observable<GenericResModel<Boolean>> {
    return this.http.post<GenericResModel<Boolean>>(FirmService.URI_FIRM + '/member', data)
      .pipe(map(incoming => {
        if (incoming.data) { this.refresh(); }
        return incoming;
      }));
  }

  public deleteTeamMember(memberId: number): Observable<GenericResModel<Boolean>> {
    return this.http.delete<GenericResModel<Boolean>>(FirmService.URI_FIRM + '/member', {body: memberId})
      .pipe(map(incoming => {
        if (incoming.data) { this.refresh(); }
        return incoming;
      }));
  }

  public changeTeamMemberStatus(data: { member: number, mode: string }): Observable<GenericResModel<Boolean>> {
    return this.http.patch<GenericResModel<Boolean>>(FirmService.URI_FIRM + '/member', data)
      .pipe(map(incoming => {
        if (incoming.data) { this.refresh(); }
        return incoming;
      }));
  }

  public onboardFirm(newData: OnboardingModel): Observable<GenericResModel<OnboardingResModel>> {
    return this.http.post<GenericResModel<OnboardingResModel>>(FirmService.URI_FIRM + '/onboarding', newData);
  }

  public getSuperAdmins(): Observable<GenericResModel<any>> {
    return this.http.get<GenericResModel<FirmUserModel[]>>(FirmService.URI_FIRM + '/super-admin');
  }

  public getMembers(): Observable<any> {
    return this.http.get<any>(FirmService.URI_FIRM + '/member');
  }

  public undoDeletion(): Observable<GenericResModel<any>> {
    return this.http.put<GenericResModel<any>>(FirmService.URI_FIRM + '/undo-delete', null).pipe(map(incoming => {
      if (incoming.data) { this.refresh(); }
      return incoming;
    }));
  }

  public pauseNextBillingCycle(firmUserHash: string): Observable<GenericResModel<any>> {
    return this.http.put<GenericResModel<any>>(FirmService.URI_FIRM + '/pause-next-billing-cycle', firmUserHash)
      .pipe(map(incoming => {
        if (incoming.data) { this.refresh(); }
        return incoming;
      }));
  }

  public undoPause(): Observable<GenericResModel<any>> {
    return this.http.put<GenericResModel<any>>(FirmService.URI_FIRM + '/undo-pause', null)
      .pipe(map(incoming => {
        if (incoming.data) { this.refresh(); }
        return incoming;
      }));
  }

  public switchFirm(firmHash: string): Observable<GenericResModel<UserModel>> {
    return this.http.put<GenericResModel<UserModel>>(FirmService.URI_FIRM + '/switch', firmHash);
  }

  public folderSize(isRefreshed: boolean): Observable<any> {
    if (isRefreshed) {
      StorageService.applicationModel.storageUsage = null;
    }
    const getStorageUsageFromService = (): Observable<any> => {
      if (StorageService.applicationModel.storageUsage) {
        return of(StorageService.applicationModel.storageUsage);
      }
    };
    if (StorageService.applicationModel.storageUsage) {
      return getStorageUsageFromService().pipe(map(usage => usage));
    } else {
      return this.http.post(environment.apiV1 + '/folder-size', null, {responseType: 'text'})
        .pipe(map(usage => this.processFolderUsage(usage)));
    }
  }

  public getFirmPartnerTeamAccess(): Observable<any> {
    return this.http.get(environment.apiV1 + '/team-access');
  }

  public deactivateAi(): Observable<GenericResModel<boolean>> {
    return this.http.delete<GenericResModel<boolean>>(environment.apiV1 + '/luca/firm-settings');
  }

  public getMasterQuestionsList(subModuleId, checkListStandardId, masterChkListId): Observable<any> {
    return this.http.get<GenericResModel<any>>(FirmService.URI_FIRM + '/master-questions-list',
      {params: {subModuleId: subModuleId, checkListStandardId: checkListStandardId, masterChkListId: masterChkListId}});
  }

  public getQuestionType(): Observable<any> {
    return this.http.get<GenericResModel<any>>(FirmService.URI_FIRM + '/question-type');
  }

  public getCategory(firmCheckListTmplId): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(FirmService.URI_FIRM + '/category', {params: {firmCheckListTmplId: firmCheckListTmplId}});
  }

  public getCheckListFolderName(): Observable<string> {
    return this.http.get<GenericResModel<string>>(environment.apiV2 + '/firm/check-list-folder-name').pipe(
      map(res => {
        if (res.status !== 200 && res.status !== 204) {
          res.errors.forEach(err => this.toaster.warning(err));
        }
        return res.data;
      })
    );
  }

  public getFirmTemplateQuestionsList(templateId: string, checklistId: any): Observable<string> {
    const params = {
      fcTemplateId: templateId
    };
    if (checklistId && checklistId != 'null') {
      params['checklist'] = checklistId;
    }
    return this.http.get<GenericResModel<string>>(FirmService.URI_FIRM + '/template-questions-list', {params: params}).pipe(
      map(res => {
        if (res.status !== 200) {
          res.errors.forEach(e => {
            this.toaster.error(e);
          });
        }
        return res.data;
      }));
  }

  public getAssignedFirms(): Observable<GenericResModel<AssignedFirmsModel[]>> {
    return this.http.get<GenericResModel<AssignedFirmsModel[]>>(FirmService.URI_ASSIGNED_FIRMS_OF_A_USER);
  }

  public getFirmPartnerAssign(): Observable<any> {
    return this.http.get(FirmService.URI_FIRM + '/firm-partner-assign');
  }

  public getAclData(userAccountId: number, typeId: number | string): Observable<any> {
    const param = {
      'userAccountId': userAccountId
    };
    if (typeId && typeId != 'undefined' && typeId != 'null') {
      param['typeId'] = typeId;
    }
    return this.http.get(FirmService.URI_FIRM + '/acl-data', {params: param});
  }

  public getFirmCountDetails(): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(FirmService.URI_FIRM + '/firm-count-details');
  }

  public getFirmComponentLibCheck(): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(FirmService.URI_FIRM + '/firm-component/library-check');
  }

  public getFirmScheduleTime(): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(FirmService.URI_FIRM + '/firm-schedule-time');
  }

  public getFirmSignature(firmUserAccountId): Observable<string> {
    return this.http.get<GenericResModel<string>>(FirmService.URI_FIRM + '/firm-signature', {params: {firmUserAccountId: firmUserAccountId}})
      .pipe(map(res => {
        if (res.status !== 200 && res.status !== 204) {
          res.errors.forEach(err => this.toaster.warning(err));
        }
        return res.data;
      }));
  }

  public getFirmFourSteps(userAccountId: number): Observable<string> {
    return this.http.get<GenericResModel<string>>(FirmService.URI_FIRM + '/four-steps', {params: {userAccountId: userAccountId}}).pipe(
      map(res => {
        if (res.status !== 200) {
          res.errors.forEach(err => this.toaster.warning(err));
        }
        return res.data;
      })
    );
  }

  public savePartnerStaff(data): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(FirmService.URI_FIRM + '/staff-assigned', this.encodingService.enData(data));
  }

  public unAssignPartnerStaff(data): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(FirmService.URI_FIRM + '/unassign-partner-staff', this.encodingService.enData(data));
  }

  public getFirmTemplate(isMaster, searchTxt): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(FirmService.URI_FIRM + '/firm-template', {
      params: {
        isMaster: isMaster,
        searchTxt: searchTxt
      }
    });
  }

  public saveFirmFooter(data): Observable<GenericResModel<number>> {
    return this.http.post<GenericResModel<number>>(FirmService.URI_FIRM + '/footer', data);
  }

  public saveFirmHeader(data): Observable<GenericResModel<number>> {
    return this.http.post<GenericResModel<number>>(FirmService.URI_FIRM + '/header', data);
  }

  public saveFirmSignature(data): Observable<GenericResModel<number>> {
    return this.http.post<GenericResModel<number>>(FirmService.URI_FIRM + '/signature', data);
  }

  public saveUserSelectedFirmHeader(userSelectedHeaderModel: UserSelectedHeaderModel): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(FirmService.URI_FIRM + '/user-selected-header', userSelectedHeaderModel);
  }

  public saveUserSelectedSignature(selectedSignatureModel: UserSelectedSignatureModel): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(FirmService.URI_FIRM + '/user-selected-signature', selectedSignatureModel);
  }

  public removeFirmTeam(): Observable<GenericResModel<boolean>> {
    return this.http.delete<GenericResModel<boolean>>(FirmService.URI_FIRM + '/remove-firm-team');
  }

  public validateMemberId(memberId: string): Observable<GenericResModel<any>> {
    return this.http.post<GenericResModel<any>>(FirmService.URI_FIRM + '/validate-member-id', memberId);
  }

  public validateLicenseNo(licenseNo: string): Observable<GenericResModel<any>> {
    return this.http.post<GenericResModel<any>>(FirmService.URI_FIRM + '/validate-license-no', licenseNo);
  }

  public validateEmail(email: string, role: number | string = null): Observable<GenericResModel<any>> {
    if (role && role != 'null') {
      return this.http.post<GenericResModel<any>>(FirmService.URI_FIRM + '/validate-email', email, {params: {'role': role}});
    } else {
      return this.http.post<GenericResModel<any>>(FirmService.URI_FIRM + '/validate-email', email);
    }
  }

  public saveFirmCheckListQuestion(data: string): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(FirmService.URI_FIRM + '/check-list-question', data);
  }

  public saveFirmCheckListMultipleChoice(data: string): Observable<string> {
    return this.http.post<GenericResModel<string>>(FirmService.URI_FIRM + '/check-list-multiple-question', data)
      .pipe(map(res => {
        if (res.status !== 200 && res.status !== 204) {
          res.errors.forEach(err => this.toaster.warning(err));
        }
        return res.data;
      }));
  }

  public getAllPartners(): Observable<FirmPartnerModel[]> {
    return this.http.get<GenericResModel<FirmPartnerModel[]>>(FirmService.URI_ALL_PARTNER_LIST).pipe(
      map(res => {
        if (res.status == 200) {
          res.data.forEach(i => i.fullname = (i.firstname + ' ' + i.lastname).trim());
          return res.data;
        }
        return null;
      })
    );
  }

  public getAllTeams(): Observable<FirmTeamModel[]> {
    return this.http.get<GenericResModel<FirmTeamModel[]>>(FirmService.URI_ALL_TEAM_LIST).pipe(
      map(res => {
        if (res.status == 200) {
          return res.data;
        }
        return null;
      })
    );
  }

  private processFolderUsage(usage) {
    StorageService.applicationModel.storageUsage = {
      size: 0,
      measure: 'MB'
    };
    if (usage && !(usage == '-')) {
      StorageService.applicationModel.storageUsage = {
        size: Math.round(parseFloat(usage.split(' ')[0])),
        measure: (usage.toLowerCase().includes('kb') ? 'KB' : (usage.toLowerCase().includes('mb') ? 'MB' : 'GB'))
      };
    }
    return StorageService.applicationModel.storageUsage;
  }

  private processFirms(firms: FirmModel[]): FirmModel {
    if (!firms) {
      return;
    }
    StorageService.applicationModel.firms = firms;
    StorageService.applicationModel.hasMultipleFirms = false;
    if (firms.length === 1) {
      return this.processFirm(firms[0]);
    }
    StorageService.applicationModel.hasMultipleFirms = true;
    StorageService.applicationModel.firm = firms.filter(e => e.hash === localStorage.getItem('firm-hash')).pop();
    return this.propagateProcessedFirm();
  }

  private processFirm(firm: FirmModel): FirmModel {
    if (!firm) {
      return;
    }
    StorageService.applicationModel.firm = firm;
    return this.propagateProcessedFirm();
  }

  private propagateProcessedFirm(): FirmModel {
    localStorage.setItem(FirmService.FIRM_KEY, JSON.stringify(StorageService.applicationModel.firm));
    this.subject.next(StorageService.applicationModel.firm);
    return StorageService.applicationModel.firm;
  }
}
